import { KVCoordinate } from 'types'

export type SortOption = 'severity' | 'spatial'

interface KestrelPoint {
  x: string
  y: string
  z: string
}

interface KestrelPointGeometry {
  kind: 'point'
  position:
    | string
    | { x: number; y: number; z: number }
    | { x: string; y: string; z: string }
  normal:
    | string
    | { x: number; y: number; z: number }
    | { x: string; y: string; z: string }
}

interface KestrelPolygonGeometry {
  kind: 'polygon'
  points: KestrelPoint[]
  normal:
    | string
    | { x: number; y: number; z: number }
    | { x: string; y: string; z: string }
}

export interface KestrelAnnotation {
  annotationId?: string
  id?: string
  annotationLabel?: string
  label?: string
  annotationNotes?: string
  notes?: string
  annotationSeverity?: string
  severity?: string
  annotationVisible?: boolean
  visible?: boolean
  userName: string
  userEmail: string
  geometry: KestrelPointGeometry | KestrelPolygonGeometry
  centeredPosition:
    | string
    | { x: number; y: number; z: number }
    | { x: string; y: string; z: string }
  centeredNormal:
    | string
    | { x: number; y: number; z: number }
    | { x: string; y: string; z: string }
  annotationImageNames?: string[]
  imageNames?: string[]
  rooftopAnnotation?: {
    section?: string
    rgbScreenshotPath?: string
    thermalScreenshotPath?: string
    whatWeSee?: string
    whyItMatters?: string
    likelyCause?: string
    recommendedFix?: string
    selected?: string[]
    [key: string]: unknown
  }
}

export interface ProcessedAnnotation extends KestrelAnnotation {
  calculatedArea?: number
  centerCoordinates: {
    x: number
    y: number
    z: number
  }
  geospatialCoordinates?: {
    lat: number
    lng: number
    elevation: number
  }
  section?: string
  evidenceType?: string
  recommendedAction?: string
  damageTypes?: string[]
  tags?: {
    inspectionArea?: {
      value?: string
    }
  }
}

const parsePositionString = (
  position:
    | string
    | { x: number; y: number; z: number }
    | { x: string; y: string; z: string }
): { x: number; y: number; z: number } => {
  if (typeof position === 'object' && position !== null) {
    return {
      x: typeof position.x === 'string' ? parseFloat(position.x) : position.x,
      y: typeof position.y === 'string' ? parseFloat(position.y) : position.y,
      z: typeof position.z === 'string' ? parseFloat(position.z) : position.z,
    }
  }

  if (typeof position === 'string') {
    const matches = position.match(
      /X=([+-]?\d*\.?\d+)\s+Y=([+-]?\d*\.?\d+)\s+Z=([+-]?\d*\.?\d+)/
    )
    if (!matches) {
      throw new Error(`Invalid position string format: ${position}`)
    }
    return {
      x: parseFloat(matches[1]),
      y: parseFloat(matches[2]),
      z: parseFloat(matches[3]),
    }
  }

  throw new Error(`Invalid position format: ${typeof position}`)
}

const calculatePolygonArea = (points: KestrelPoint[]): number => {
  if (points.length < 3) return 0

  let area = 0
  const n = points.length

  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n
    const xi = parseFloat(points[i].x)
    const yi = parseFloat(points[i].y)
    const xj = parseFloat(points[j].x)
    const yj = parseFloat(points[j].y)

    area += xi * yj - xj * yi
  }

  return Math.abs(area) / 2 / 929.03
}

const calculatePolygonCenter = (
  points: KestrelPoint[]
): { x: number; y: number; z: number } => {
  if (points.length === 0) {
    return { x: 0, y: 0, z: 0 }
  }

  const sum = points.reduce(
    (acc, point) => ({
      x: acc.x + parseFloat(point.x),
      y: acc.y + parseFloat(point.y),
      z: acc.z + parseFloat(point.z),
    }),
    { x: 0, y: 0, z: 0 }
  )

  return {
    x: sum.x / points.length,
    y: sum.y / points.length,
    z: sum.z / points.length,
  }
}

export const getAnnotationSeverity = (
  annotation: KestrelAnnotation
): string => {
  const severity = annotation.annotationSeverity || annotation.severity
  if (!severity || severity.trim() === '') {
    return 'Unknown'
  }
  return severity.trim()
}

export const getAnnotationLabel = (annotation: KestrelAnnotation): string => {
  return annotation.annotationLabel || annotation.label || ''
}

export const getAnnotationNotes = (annotation: KestrelAnnotation): string => {
  return annotation.annotationNotes || annotation.notes || ''
}

export const getAnnotationId = (annotation: KestrelAnnotation): string => {
  return annotation.annotationId || annotation.id || ''
}

export const getAnnotationDamageTypes = (
  annotation: KestrelAnnotation
): string[] => {
  if (!annotation.rooftopAnnotation) {
    return []
  }
  const selected = annotation.rooftopAnnotation.selected
  return Array.isArray(selected) ? selected : []
}

export const processAnnotation = (
  annotation: KestrelAnnotation,
  origin?: KVCoordinate,
  measurementScale: number = 1
): ProcessedAnnotation => {
  let centerCoordinates: { x: number; y: number; z: number }
  let calculatedArea: number | undefined

  try {
    if (annotation.geometry.kind === 'polygon') {
      centerCoordinates = calculatePolygonCenter(annotation.geometry.points)

      if ((annotation as { area?: number }).area !== undefined) {
        calculatedArea = (annotation as { area?: number }).area! / 929.03
      } else {
        calculatedArea = calculatePolygonArea(annotation.geometry.points)
      }
    } else {
      centerCoordinates = parsePositionString(annotation.geometry.position)
    }
  } catch (error) {
    console.error('Error parsing annotation geometry:', error, annotation)
    centerCoordinates = { x: 0, y: 0, z: 0 }
  }

  let geospatialCoordinates:
    | { lat: number; lng: number; elevation: number }
    | undefined

  if (origin) {
    const geoCoord = KVCoordinate.fromWorldCoordinate(
      centerCoordinates,
      origin,
      measurementScale
    )

    geospatialCoordinates = {
      lat: geoCoord.lat,
      lng: geoCoord.long,
      elevation: geoCoord.elevation,
    }
  }

  const section = annotation.rooftopAnnotation?.section

  const processedAnnotation: ProcessedAnnotation = {
    ...annotation,
    annotationId: getAnnotationId(annotation),
    annotationLabel: getAnnotationLabel(annotation),
    annotationNotes: getAnnotationNotes(annotation),
    annotationSeverity: getAnnotationSeverity(annotation),
    centerCoordinates,
    geospatialCoordinates,
    calculatedArea,
    section,
    damageTypes: getAnnotationDamageTypes(annotation),
  }

  return processedAnnotation
}

export const sortAnnotations = (
  a: ProcessedAnnotation,
  b: ProcessedAnnotation,
  sortBy: SortOption
): number => {
  if (sortBy === 'severity') {
    const severityA = parseInt(getAnnotationSeverity(a), 10) || 0
    const severityB = parseInt(getAnnotationSeverity(b), 10) || 0
    return severityB - severityA
  } else {
    const yDiff = b.centerCoordinates.y - a.centerCoordinates.y
    if (Math.abs(yDiff) > 0.1) {
      return yDiff
    }
    return a.centerCoordinates.x - b.centerCoordinates.x
  }
}

export const isPolygonAnnotation = (
  annotation: ProcessedAnnotation
): annotation is ProcessedAnnotation & { calculatedArea: number } => {
  return (
    annotation.geometry.kind === 'polygon' &&
    annotation.calculatedArea !== undefined
  )
}

export const isPointAnnotation = (annotation: ProcessedAnnotation): boolean => {
  return annotation.geometry.kind === 'point'
}

export const formatCoordinates = (
  coordinates: { x: number; y: number; z: number },
  decimals: number = 2
): string => {
  return `X=${coordinates.x.toFixed(decimals)} Y=${coordinates.y.toFixed(decimals)} Z=${coordinates.z.toFixed(decimals)}`
}

export const formatArea = (area: number, decimals: number = 2): string => {
  return `${area.toFixed(decimals)} m²`
}

export const formatGeospatialCoordinates = (
  coordinates: { lat: number; lng: number; elevation: number },
  decimals: number = 6
): string => {
  return `${coordinates.lat.toFixed(decimals)}, ${coordinates.lng.toFixed(decimals)}`
}
